import fp from "lodash/fp";
import moment from "moment";

import {
	checkAddressFieldsEmpty,
	formatE164,
	getFullName,
	getStringWithoutHyphens,
} from "helpers";
import {
	mapAddressFromApi,
	mapAddressToApi,
	mapOwnerToApi,
	parseOwner,
} from "mappers";
import { getSelectedMonths } from "models";
import { ClosureReasonsHistorical, PriorityTypes } from "types";
import { taxIdNormalizerAsterisk } from "validators";

export const mapApiMerchantToFrontMerchant = model => {
	const owners = fp.flow(
		fp.filter(fp.identity),
		fp.map(data => parseOwner(data, true)),
	)([model.owner1, model.owner2, model.owner3, model.owner4]);

	const signer = model.authorizedSigner
		? parseOwner(model.authorizedSigner, true)
		: {};

	const businessAddress = mapAddressFromApi(model?.businessAddress);
	const legalAddress = mapAddressFromApi(model?.legalAddress);
	const shippingAddress = mapAddressFromApi(model?.shippingAddress);

	const generalData = {
		...model,
		closeDate:
			model.closeDate && moment(model.closeDate).format("MM/DD/YYYY"),
		contractEndDate:
			model.contractEndDate &&
			moment(model.contractEndDate).format("MM/DD/YYYY"),
		activeDate:
			model.activeDate && moment(model.activeDate).format("MM/DD/YYYY"),
		approvalDate:
			model.approvalDate &&
			moment(model.approvalDate).format("MM/DD/YYYY"),
		lastBatch:
			model.lastBatch && moment(model.lastBatch).format("MM/DD/YYYY"),
		businessEstablishedDate:
			model.businessEstablishedDate &&
			moment(model.businessEstablishedDate).format("MM/DD/YYYY"),
		csPhone: model.csPhoneNumbers,
		isMerchantOptsOutOfReceivingFutureCommercialFromAmex:
			!!model.isMerchantOptsOutOfReceivingFutureCommercialFromAmex,
		doYouUseAThirdPartyForFulfillment:
			model.doYouUseAThirdPartyForFulfillment?.toString(),
		recurringPayments: model.recurringPayments?.toString(),
		isAuroraHouseAccount: model.isAuroraHouseAccount?.toString(),
		isvId: model.isv?.id?.toString(),
		seasonalMonths: getSelectedMonths(model.seasonalMonths),
		taxId: taxIdNormalizerAsterisk(model.taxId),
		agent: model.agent && {
			...model.agent,
			fullName: getFullName(model.agent),
		},
		processorSponsorSection: {
			processor: model.processor,
			sponsor: model.sponsor,
			achProcessor: model.achProcessor,
		},
		primaryContactSection: {
			...model.primaryContact,
			isPrimaryContactTheSame: model.isPrimaryContactTheSame?.toString(),
		},
		businessAddress,
		legalAddress: {
			...legalAddress,
			isLegalAddressTheSame: model?.isLegalAddressTheSame?.toString(),
		},
		shippingAddress: {
			...shippingAddress,
			isShippingAddressTheSame:
				model?.isShippingAddressTheSame?.toString(),
		},
		mccCodeSection: {
			industryType: model.industryType,
			mccCode: model.mccCode,
			detailedExplanationWhatDoYouSell:
				model.detailedExplanationWhatDoYouSell,
		},
		ownerPrincipalSection: {
			owners,
			signer,
		},
	};

	delete generalData.owner1;
	delete generalData.owner2;
	delete generalData.owner3;
	delete generalData.owner4;
	delete generalData.authorizedSigner;

	return generalData;
};

export const mapMerchantFormToModel = (
	generalEditFormValues,
	ticketsFormValues = {},
) => {
	const closureReasonsHistorical =
		ClosureReasonsHistorical.getOptions().filter(
			option =>
				option.displayName ===
				generalEditFormValues.accountClosureReason,
		)?.[0];
	const isClosureReasonsHistorical = !fp.isEmpty(closureReasonsHistorical);
	const closureReason = isClosureReasonsHistorical
		? closureReasonsHistorical.value
		: generalEditFormValues.accountClosureReason || "none";

	const signerExists =
		generalEditFormValues?.ownerPrincipalSection?.signer?.id;

	const businessAddress = mapAddressToApi(
		generalEditFormValues?.businessAddress,
	);
	const legalAddress = mapAddressToApi(
		generalEditFormValues?.legalAddress,
		checkAddressFieldsEmpty,
	);
	const isLegalAddressTheSame =
		generalEditFormValues.legalAddress.isLegalAddressTheSame === "true" ||
		generalEditFormValues.legalAddress.isLegalAddressTheSame === true;
	const shippingAddress = mapAddressToApi(
		generalEditFormValues?.shippingAddress,
		checkAddressFieldsEmpty,
	);
	const isShippingAddressTheSame =
		generalEditFormValues.shippingAddress.isShippingAddressTheSame ===
			"true" ||
		generalEditFormValues.shippingAddress.isShippingAddressTheSame === true;

	const owner1 = mapOwnerToApi(
		generalEditFormValues?.ownerPrincipalSection?.owners[0],
	);
	const owner2 = generalEditFormValues?.ownerPrincipalSection?.owners[1]
		? mapOwnerToApi(generalEditFormValues?.ownerPrincipalSection?.owners[1])
		: null;
	const owner3 = generalEditFormValues?.ownerPrincipalSection?.owners[2]
		? mapOwnerToApi(generalEditFormValues?.ownerPrincipalSection?.owners[2])
		: null;
	const owner4 = generalEditFormValues?.ownerPrincipalSection?.owners[3]
		? mapOwnerToApi(generalEditFormValues?.ownerPrincipalSection?.owners[3])
		: null;
	const authorizedSigner = signerExists
		? mapOwnerToApi(generalEditFormValues?.ownerPrincipalSection?.signer)
		: null;

	return {
		...ticketsFormValues,
		...generalEditFormValues,
		...generalEditFormValues.mccCodeSection,
		agent: generalEditFormValues.agent && {
			...generalEditFormValues.agent,
			phoneNumber:
				formatE164(generalEditFormValues.agent?.phoneNumber) || null,
		},
		businessPhone: formatE164(generalEditFormValues?.businessPhone) || null,
		csPhone: formatE164(generalEditFormValues?.csPhone) || null,
		seasonalMonths: generalEditFormValues?.seasonalMonths?.map(
			({ value }) => value,
		),
		businessAddress,
		legalAddress: isLegalAddressTheSame ? null : legalAddress,
		shippingAddress: isShippingAddressTheSame ? null : shippingAddress,
		isShippingAddressTheSame,
		isLegalAddressTheSame,
		priorityType:
			PriorityTypes.getOptions({
				predicate: item =>
					item.name === generalEditFormValues.priorityType,
			})?.[0]?.value || 0,
		mid: generalEditFormValues.mid || null,
		taxId: getStringWithoutHyphens(generalEditFormValues.taxId) || null,
		originalMID: generalEditFormValues.originalMID || null,
		discoveryMID: generalEditFormValues.discoverMid || null,
		headquarterId: generalEditFormValues.headquarters?.id,
		isPrimaryContactTheSame:
			generalEditFormValues.primaryContactSection.isPrimaryContactTheSame,
		primaryContact: generalEditFormValues.primaryContactSection,
		owner1,
		owner2,
		owner3,
		owner4,
		authorizedSigner,
		processorId: generalEditFormValues.processorSponsorSection.processor.id,
		achProcessor:
			generalEditFormValues.processorSponsorSection.achProcessor,
		sponsorId: generalEditFormValues.processorSponsorSection.sponsor.id,
		statusId: generalEditFormValues.merchantStatus?.id,
		isvId: generalEditFormValues?.isvId || null,
		company: {
			...generalEditFormValues.company,
			csPhone: formatE164(generalEditFormValues.company?.csPhone) || null,
			id: generalEditFormValues.company.id.value,
		},
		supportOption: generalEditFormValues.supportOption || "None",

		processor: undefined,
		sponsor: undefined,
		merchantStatus: undefined,
		processorSponsorSection: undefined,
		mccCodeSection: undefined,
		ownerPrincipalSection: undefined,
		primaryContactSection: undefined,
		headquarters: undefined,
		activeDate: undefined,

		accountClosureReason: closureReason,
	};
};
